import React, {useCallback, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {
  getCollaboratorMember,
  showModalLimitedAccess,
  showModalReprocessKyc,
  showSnackbar,
  useHistory,
} from 'utils'
import {requestData} from 'services'
import {useWindowLayout} from 'windows'
import {
  ForkygramPostActionOptions,
  ForkygramPostActionOptionsFeedFotoTree,
  ForkygramReportCategoriesResponse,
} from 'types'
import {
  GiftShopTemplateOptionsContent,
  GiftShopTemplateOptionsItem,
  GiftShopTemplateOptionsModal,
  GiftShopTemplateReportModal,
  GiftShopTemplateShareModal,
} from 'pages/gift-shop'
import {useSelector} from 'lib/redux'
import {ConfirmationModal} from 'common/components'
import {TemplateProfileSelfOptionModal} from '../../../../template'
import {FototreeMemoryContentProps} from './FototreeContentProps'
import {FototreeMemoryContentItem} from '../item'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

export default function FototreeMemoryContent({
  data,
  dataTag,
  stateMute,
  statePause,
  index,
  scrollPosition,
  componentRef,
  onReported,
  onEdited,
  onFollowed,
  onCollabRemoved,
}: FototreeMemoryContentProps) {
  const {translate} = useTranslation()
  const history = useHistory()
  const {height} = useWindowLayout()
  const {id} = useSelector('user') || {}
  const {kycStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {status: childStatus} = useSelector('parentalConsentState')
  const pause = statePause[0]
  const [selfModal, setSelfModal] = useState<ForkygramPostActionOptions>('none')
  const [modal, setModal] = useState<ForkygramPostActionOptionsFeedFotoTree>(
    'none',
  )
  const stateRot = useState(0)
  const [rot, setRot] = stateRot
  const currentRot = useRef(0)

  const {collaboratorOwner, collaboratorSelf} = getCollaboratorMember(
    data.collaborator_members,
  )

  const handleToggleModal = useCallback(() => {
    setSelfModal('none')
    setModal('none')
  }, [])

  const handleReportPost = useCallback(
    (category: ForkygramReportCategoriesResponse, description?: string) => {
      requestData('forkygram_post_report_posts', {
        data: {
          category_id: category.id,
          description,
          reported_id: data.post_id,
          reported_member_id: collaboratorOwner.id,
        },
        onRequestReceived: () => handleToggleModal(),
        onRequestFailed: () =>
          showSnackbar(translate('forkygram:reportPostFailed')),
        onRequestSuccess: ({status}) => {
          onReported(data.post_id)
          showSnackbar(
            translate(
              status === 200
                ? 'forkygram:reportPostSuccess'
                : 'forkygram:reportPostFailed',
            ),
          )
        },
      })
    },
    [
      collaboratorOwner.id,
      data.post_id,
      handleToggleModal,
      onReported,
      translate,
    ],
  )

  const handleDeletePost = useCallback(() => {
    requestData('forkygram_delete_post', {
      actionType: 'execute',
      useDefaultMessage: true,
      data: {id: data.post_id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('giftShop:removeContentsSnackbarSuccess'))
          history.goBack()
        }
      },
    })
  }, [data.post_id, history, translate])

  const handleRemoveCollaboration = useCallback(() => {
    if (!collaboratorSelf) return
    requestData('forkygram_delete_collaborator', {
      useDefaultMessage: true,
      data: {post_id: data.post_id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('tree:removeCollaborationSuccessMessage'))
          onCollabRemoved(collaboratorSelf.id)
        }
      },
    })
  }, [collaboratorSelf, data.post_id, onCollabRemoved, translate])

  const handleClickMore = useCallback(() => {
    if (id === collaboratorOwner.id) setSelfModal('more')
    else setModal('options')
  }, [collaboratorOwner.id, id])

  const handleFeatureDisabled = useCallback(() => {
    handleToggleModal()
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, handleToggleModal, history, kycStatus, similarStatus])

  const handleRotateContent = useCallback(
    (dir: 'left' | 'right' | 'flip') => {
      // preview set to CCW
      if (dir === 'flip') {
        setRot((prev) => (prev - 180) % 360)
        currentRot.current += 180
      } else if (dir === 'left') {
        setRot((prev) => (prev - 90) % 360)
        currentRot.current += 90
      } else if (dir === 'right') {
        setRot((prev) => (prev - 270) % 360)
        currentRot.current += 270
      }
    },
    [setRot],
  )

  const handleFinalizeRotateModal = useCallback(() => {
    handleToggleModal()
    if (currentRot.current % 360 !== 0) {
      requestData('forkygram_rotate_post', {
        data: {post_id: data.post_id, rotation: currentRot.current % 360},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            showSnackbar(translate('global:success'))
          } else {
            setRot(rot + (currentRot.current % 360))
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
          currentRot.current = 0
        },
      })
    }
  }, [data.post_id, handleToggleModal, rot, setRot, translate])

  const handleRenderOptionsModal = useMemo(
    () => (
      <GiftShopTemplateOptionsModal
        onShare={() =>
          childStatus ||
          !(kycStatus === 'verified' || kycStatus === 'verified_check')
            ? handleFeatureDisabled()
            : setModal('share')
        }
        onDelete={
          data.can_delete
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('delete')
            : undefined
        }
        onEdit={
          data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : onEdited()
            : undefined
        }
        onReport={
          !data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('report')
            : undefined
        }
        onRemoveCollab={
          collaboratorSelf
            ? () => {
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('remove-collaboration')
              }
            : undefined
        }
        toggleModal={handleToggleModal}
        onRotate={() => setModal('rotate')}
      />
    ),
    [
      childStatus,
      collaboratorSelf,
      data.can_delete,
      data.can_edit,
      handleFeatureDisabled,
      handleToggleModal,
      kycStatus,
      onEdited,
    ],
  )

  const handleRenderShareModal = useMemo(
    () =>
      dataTag?.tree_id && (
        <GiftShopTemplateShareModal
          type="leaf"
          targetId={dataTag.tree_id}
          subTargetId={data.post_id}
          caption={data.caption}
          toggleModal={handleToggleModal}
        />
      ),
    [data.caption, data.post_id, dataTag?.tree_id, handleToggleModal],
  )

  const handleRenderReportModal = useMemo(
    () => (
      <GiftShopTemplateReportModal
        type="post"
        translationKey="giftShop:reportReason"
        onSubmit={handleReportPost}
        toggleModal={() => {
          handleToggleModal()
          showSnackbar(translate('forkygram:reportCancel'))
        }}
      />
    ),
    [handleReportPost, handleToggleModal, translate],
  )

  const handleRenderDeleteModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('forkygram:deletePostTitle')}
        text={translate('forkygram:deletePostMessage')}
        onConfirm={handleDeletePost}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:delete')}
      />
    ),
    [handleDeletePost, handleToggleModal, translate],
  )

  const handleRenderMoreModal = useMemo(
    () => (
      <TemplateProfileSelfOptionModal
        visible
        toggleModal={handleToggleModal}
        onClickDelete={() => setSelfModal('delete')}
        onClickShare={() => {
          if (childStatus) {
            handleToggleModal()
            showModalLimitedAccess()
            return
          }
          setSelfModal('share')
        }}
        onClickEdit={onEdited}
        onRotate={() => setSelfModal('rotate')}
      />
    ),
    [childStatus, handleToggleModal, onEdited],
  )

  const handleRenderColaborationModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('tree:removeCollaboration')}
        text={translate('tree:removeCollaborationDescription')}
        onConfirm={handleRemoveCollaboration}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:remove')}
      />
    ),
    [handleRemoveCollaboration, handleToggleModal, translate],
  )

  const handleRenderRotateModal = useMemo(
    () => (
      <GiftShopTemplateOptionsContent
        visible
        toggleModal={handleFinalizeRotateModal}>
        <GiftShopTemplateOptionsItem
          icon="rotate-left"
          text={translate('global:rotateLeft')}
          onClickItem={() => {
            handleRotateContent('left')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="rotate-right"
          text={translate('global:rotateRight')}
          onClickItem={() => {
            handleRotateContent('right')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="flip-camera"
          text={translate('global:flipVertical')}
          onClickItem={() => {
            handleRotateContent('flip')
          }}
        />
      </GiftShopTemplateOptionsContent>
    ),
    [handleFinalizeRotateModal, handleRotateContent, translate],
  )

  const handleRenderModal = useMemo(() => {
    switch (modal) {
      case 'options':
        return handleRenderOptionsModal
      case 'remove-collaboration':
        return handleRenderColaborationModal
      case 'report':
        return handleRenderReportModal
      case 'delete':
        return handleRenderDeleteModal
      case 'share':
        return handleRenderShareModal
      case 'rotate':
        return handleRenderRotateModal
      default:
        return undefined
    }
  }, [
    handleRenderColaborationModal,
    handleRenderDeleteModal,
    handleRenderOptionsModal,
    handleRenderReportModal,
    handleRenderRotateModal,
    handleRenderShareModal,
    modal,
  ])

  const handleRenderSelfModal = useMemo(() => {
    switch (selfModal) {
      case 'more':
        return handleRenderMoreModal
      case 'delete':
        return handleRenderDeleteModal
      case 'share':
        return handleRenderShareModal
      case 'rotate':
        return handleRenderRotateModal
      case 'remove-collaboration':
      case 'none':
        return null
    }
  }, [
    handleRenderDeleteModal,
    handleRenderMoreModal,
    handleRenderRotateModal,
    handleRenderShareModal,
    selfModal,
  ])

  const scrollPos = useMemo(() => scrollPosition + 0.2 * height, [
    height,
    scrollPosition,
  ])

  const focused = useMemo(() => Math.floor(scrollPos / height) === index, [
    height,
    index,
    scrollPos,
  ])

  const handleRenderContent = useMemo(
    () => (
      <FototreeMemoryContentItem
        data={data}
        dataTag={dataTag}
        focused={focused}
        active={focused && !pause}
        stateMute={stateMute}
        statePause={statePause}
        onClickMore={handleClickMore}
        onFollow={onFollowed}
        stateRot={stateRot}
      />
    ),
    [
      data,
      dataTag,
      focused,
      handleClickMore,
      onFollowed,
      pause,
      stateMute,
      statePause,
      stateRot,
    ],
  )

  return (
    <StyledContainer ref={componentRef}>
      {handleRenderSelfModal}
      {handleRenderModal}
      {handleRenderContent}
    </StyledContainer>
  )
}
